import React from "react";
import { TableProps, Tag, Button, Popconfirm, Tooltip, Rate } from "antd";
import { Pencil, Trash2 } from "lucide-react";
import { IReview } from "../../../types/api/review";

interface GetReviewColumnsProps {
  onEdit: (record: IReview) => void;
  onDelete: (id: string) => void;
}

export const getReviewColumns = ({
  onEdit,
  onDelete,
}: GetReviewColumnsProps): TableProps<IReview>["columns"] => [
  {
    title: "Avatar & Name",
    key: "name",
    render: (_, record) => (
      <div className="flex items-center gap-3">
        {record.avatar ? (
          <img
            src={record.avatar}
            alt={record.name}
            className="w-9 h-9 rounded-full object-cover border border-slate-200"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center text-sm">
            {record.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <div className="font-medium text-slate-800">{record.name}</div>
          <div className="text-xs text-slate-500">{record.role || "Customer"}</div>
        </div>
      </div>
    ),
  },
  {
    title: "Rating",
    dataIndex: "rating",
    key: "rating",
    render: (rating: number) => <Rate disabled defaultValue={rating} className="text-xs" />,
  },
  {
    title: "Comment",
    dataIndex: "comment",
    key: "comment",
    ellipsis: true,
    render: (text: string) => (
      <span className="text-slate-600 text-sm">{text}</span>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 100,
    render: (status: boolean) => (
      <Tag color={status ? "green" : "red"}>{status ? "Active" : "Inactive"}</Tag>
    ),
  },
  {
    title: "Action",
    key: "action",
    width: 100,
    render: (_, record) => (
      <div className="flex items-center gap-1">
        <Tooltip title="Edit">
          <Button
            size="small"
            icon={<Pencil size={14} />}
            onClick={() => onEdit(record)}
            className="text-slate-500 hover:text-blue-600 border-none shadow-none bg-transparent"
          />
        </Tooltip>
        <Tooltip title="Delete">
          <Popconfirm
            title="Delete this review?"
            description="This action cannot be undone."
            onConfirm={() => onDelete(record._id)}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Button
              size="small"
              icon={<Trash2 size={14} />}
              className="text-slate-500 hover:text-red-600 border-none shadow-none bg-transparent"
            />
          </Popconfirm>
        </Tooltip>
      </div>
    ),
  },
];
